import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileuploadComponent } from '../../fileupload/fileupload.component';
import { Orgfile } from '../../fileupload/model/orgfile';
import { FormField, FileRef } from '../models/form.model';
import { FormValidationService } from '../service/form-validation.service';

@Component({
  selector: 'app-form-file-upload-field',
  standalone: true,
  imports: [CommonModule, FileuploadComponent],
  template: `
    <div class="form-group">
      <div *ngIf="field.helpText" class="help-text">{{ field.helpText }}</div>

      <app-fileupload
        [orgfile]="mappedOrgfile"
        [organizationId]="organizationId"
        [projectId]="projectId || ''"
        [index]="taskId || projectId || field.id"
        [taskInputName]="field.label + (field.required ? ' *' : '')"
        [filetype]="filetype"
        [customerId]="customerId"
        [visibility]="visibility"
        [modal]="false"
        [filename]="field.label"
        [downloadtoggle]="!!value?.url"
        (fileUploaded)="handleFileUploaded($event)"
        (fileDeleted)="handleFileDeleted()"
        (filelistUpdated)="handleFileListUpdated()"
      ></app-fileupload>

      <div *ngIf="hasError && error" class="invalid-feedback">
        {{ validationService.getErrorMessage(field, error) }}
      </div>
    </div>
  `,
  styles: [`
    .form-group { margin-bottom: 1rem; }
    .help-text { font-size: 0.875rem; color: #6c757d; margin-bottom: 0.5rem; }
    .invalid-feedback { color: #dc3545; font-size: 0.875rem; margin-top: 0.25rem; display: block; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormFileUploadFieldComponent implements OnChanges {
  @Input() field!: FormField;
  @Input() value: FileRef | undefined;
  @Input() organizationId!: string;
  @Input() projectId?: string;
  @Input() taskId?: string;
  @Input() customerId?: string;
  @Input() visibility: string = 'private';
  @Input() filetype: string = 'form';

  @Output() valueChange = new EventEmitter<FileRef | undefined>();
  @Output() errorChange = new EventEmitter<any>();

  error: any = null;
  hasError = false;
  mappedOrgfile: Orgfile | undefined;

  constructor(public validationService: FormValidationService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.mappedOrgfile = this.value ? this.mapFileRefToOrgfile(this.value) : undefined;
      this.cdr.markForCheck();
    }
    this.updateValidation();
  }

  handleFileUploaded(orgfile: Orgfile | null): void {
    if (orgfile) {
      const fileRef = this.mapOrgfileToFileRef(orgfile);
      this.value = fileRef;
      this.mappedOrgfile = orgfile; // Update mapped orgfile so download works
      this.valueChange.emit(fileRef);
    } else {
      this.value = undefined;
      this.mappedOrgfile = undefined;
      this.valueChange.emit(undefined);
    }
    this.updateValidation();
  }

  handleFileDeleted(): void {
    this.value = undefined;
    this.mappedOrgfile = undefined;
    this.valueChange.emit(undefined);
    this.updateValidation();
  }

  handleFileListUpdated(): void {
    this.updateValidation();
  }

  private updateValidation(): void {
    if (!this.field) return;
    this.error = this.validationService.validateField(this.field, this.value);
    this.hasError = !!this.error;
    this.errorChange.emit(this.error);
  }

  private mapOrgfileToFileRef(orgfile: Orgfile): FileRef {
    return {
      fileId: orgfile.fileId || '',
      filename: orgfile.filename,
      url: orgfile.fileurl,
      contentType: '',
      size: (orgfile as any).size || 0
    };
  }

  private mapFileRefToOrgfile(fileRef: FileRef): Orgfile {
    const orgfile = new Orgfile();
    orgfile.fileId = fileRef.fileId;
    orgfile.filename = fileRef.filename;
    orgfile.fileurl = fileRef.url;
    orgfile.organizationId = this.organizationId;
    orgfile.projectId = this.projectId || '';
    orgfile.filetype = this.filetype;
    orgfile.visibility = this.visibility;
    return orgfile;
  }
}
